import { useCallback, useEffect, useRef, useState } from 'react';
import { readExtras, writeExtras, request } from './plannerStorage';
import { dateKey } from './savings';

const dateOnly = value => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : dateKey(date);
};
export function fromSheet(row, extra = {}) {
  const entries = Array.isArray(extra.entries) ? extra.entries : [];
  const total = entries.reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
  return {
    ...extra, id: String(row.ID), name: row.Name || '', price: Number(row.TargetAmount) || 0,
    initial: (Number(row.CurrentAmount) || 0) - total, entries,
    due: dateOnly(row.EndDate), mode: 'date', months: extra.months || 6,
    url: extra.url || '', qr: extra.qr || '', recipient: extra.recipient || '',
    sheet: row,
  };
}
export function toSheet(goal) {
  const row = goal.sheet || {};
  return {
    id: row.ID ?? null, name: goal.name, targetAmount: Number(goal.price),
    currentAmount: Math.round((Number(goal.initial) + goal.entries.reduce((sum, e) => sum + Number(e.amount), 0)) * 100) / 100,
    startDate: row.ID !== undefined ? (row.StartDate ?? '') : dateKey(),
    endDate: dateOnly(row.EndDate) === goal.due ? (row.EndDate || '') : goal.due,
    color: row.Color ?? '#10B981', note: row.Note ?? '',
  };
}
function metadata(goal) {
  return { entries: goal.entries, url: goal.url, qr: goal.qr, recipient: goal.recipient,
    months: goal.months, purchased: Boolean(goal.purchased) };
}
export function useSheetGoals(apiUrl) {
  const [goals, setGoals] = useState([]);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [available, setAvailable] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [revision, setRevision] = useState(0);
  const saving = useRef(false);
  const reload = useCallback(() => setRevision(n => n + 1), []);
  useEffect(() => {
    let active = true;
    setLoaded(false); setError(''); setAvailable(null);
    const load = async () => {
      try {
        const rows = await request(apiUrl, 'getGoals');
        if (!Array.isArray(rows)) throw Error('รูปแบบข้อมูลเป้าหมายไม่ถูกต้อง');
        const extras = readExtras(apiUrl);
        if (!active) return;
        setGoals(rows.map(row => fromSheet(row, extras.goals[String(row.ID)])));
        setAccounts(extras.accounts); setLoaded(true);
      } catch (e) { if (active) setError(e.message); }
    };
    load();
    request(apiUrl, 'getPlanner').then(data => {
      if (active && data && Array.isArray(data.expenses || [])) {
        setAvailable((Number(data.income) || 0) - (data.expenses || []).reduce((sum, e) => sum + (Number(e.amount) || 0), 0));
      }
    }).catch(() => {});
    return () => { active = false; };
  }, [apiUrl, revision]);
  async function save(next) {
    if (!loaded || saving.current) return false;
    saving.current = true; setBusy(true); setError('');
    let posted = false;
    try {
      const removed = goals.find(g => !next.some(n => n.id === g.id));
      const changed = next.find(n => !goals.includes(n));
      if (!removed && !changed) return true;
      const extras = readExtras(apiUrl);
      const extraGoals = { ...extras.goals };
      // Preflight browser storage before sending any change to Sheets.
      if (changed) {
        const candidate = { ...extras, goals: { ...extraGoals, [changed.id]: metadata(changed) } };
        const probeKey = `appv2-storage-check:${apiUrl}`;
        try { localStorage.setItem(probeKey, JSON.stringify(candidate)); }
        finally { localStorage.removeItem(probeKey); }
      }
      if (removed) {
        posted = true;
        await request(apiUrl, 'deleteGoal', { id: removed.sheet.ID });
        delete extraGoals[removed.id];
        writeExtras(apiUrl, { goals: extraGoals });
        setGoals(next);
      } else {
        const existing = goals.find(g => g.id === changed.id);
        const payload = toSheet(changed);
        const cloudChanged = !existing || JSON.stringify(payload) !== JSON.stringify(toSheet(existing));
        if (cloudChanged) {
          posted = true;
          await request(apiUrl, existing ? 'updateGoal' : 'addGoal', payload);
        }
        let row = existing?.sheet;
        if (!existing) {
          const rows = await request(apiUrl, 'getGoals');
          if (!Array.isArray(rows)) throw Error('อ่านเป้าหมายหลังบันทึกไม่สำเร็จ');
          const matches = rows.filter(r => !goals.some(g => g.id === String(r.ID)) && r.Name === changed.name && Number(r.TargetAmount) === Number(changed.price));
          if (matches.length !== 1) throw Error('บันทึกแล้ว แต่ระบุรายการใหม่ไม่ได้ กรุณาโหลดอีกครั้ง');
          row = matches[0];
        }
        const updatedRow = { ...row, Name: payload.name, TargetAmount: payload.targetAmount,
          CurrentAmount: payload.currentAmount, StartDate: payload.startDate, EndDate: payload.endDate,
          Color: payload.color, Note: payload.note };
        extraGoals[String(updatedRow.ID)] = metadata(changed);
        writeExtras(apiUrl, { goals: extraGoals });
        const updated = fromSheet(updatedRow, extraGoals[String(updatedRow.ID)]);
        setGoals(next.map(g => g.id === changed.id ? updated : g));
      }
      return true;
    } catch (e) {
      setError(posted ? `${e.message} • โปรดโหลดข้อมูลอีกครั้งเพื่อตรวจผลก่อนทำรายการซ้ำ` : `ข้อมูลยังไม่ถูกส่ง: ${e.message}`);
      // An uncertain POST must be reconciled by reading Sheets before another write.
      if (posted) setLoaded(false);
      return false;
    } finally { saving.current = false; setBusy(false); }
  }
  return { goals, save, error, setError, loaded, busy, reload, available, accounts };
}
